<template>
  <form
    action="."
    class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-16"
  >
    <!--
      NOTE: Wrapping this component with an empty <form> tag
      enables the "Search" button on the iOS keyboard. The
      tag serves no other functional purpose.
    -->
    <cf-select-input
      ref="selectInput"
      v-model="selectedGooglePlaceId"
      :label="labelDisplay"
      :loading="loadingLocationData || loading"
      :placeholder="placeholder"
      :values="locations"
      :class="isInline && 'cf-select-input--inline'"
      name="searchLocations"
      @search-term-input="searchTermUpdateHandler"
      @search-term-focus="isFocused = true"
      @search-term-blur="isFocused = false"
      @unmatched-input="unmatchedInputHandler"
    />

    <div>
      <button
        v-tippy="{ content: currentLocationTooltip, delay: 300 }"
        class="cf-button cf-button--tertiary cf-button--fixed-32"
        type="button"
        :aria-pressed="fetchedCurrentLocation"
        @click="getCurrentPosition"
      >
        <span :class="currentLocationIconClass" />

        <span class="ada-text">
          {{ currentLocationTooltip }}
        </span>
      </button>
    </div>
  </form>
</template>



<script>
  import debounce from 'debounce';

  import events from '@/services/events';
  import logger from '@/services/logger';

  import CfSelectInput from '@/view/components/shared/cf-select-input/index.vue';


  export default {
    name: 'CfGeolocationInput',

    components: {
      CfSelectInput
    },

    props: {
      isInline: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: 'Search'
      },
      loading: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: 'City, ZIP, or address...'
      }
    },

    data() {
      return {
        fetchedCurrentLocation: false,
        fetchingCurrentLocation: false,
        loadingLocationData: false,
        locations: [],
        searchTerm: null,
        selectedGooglePlaceId: null,
        isFocused: false
      };
    },

    computed: {
      labelDisplay() {
        return this.fetchedCurrentLocation && !this.isFocused ? 'Your Current Location' : this.label;
      },

      currentLocationTooltip() {
        return this.fetchedCurrentLocation
          ? 'Location sharing enabled'
          : 'Location sharing disabled';
      },

      currentLocationIconClass() {
        return this.fetchedCurrentLocation
          ? 'cf-icon far fa-location cf-icon--brand-primary'
          : 'cf-icon far fa-location-slash';
      }
    },

    watch: {
      selectedGooglePlaceId: 'googlePlaceSelected'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
      },

      genericErrorHandler() {
        events.emit('notification', {
          type: 'error',
          message: 'Couldn\'t calculate your search location. Please try again.'
        });
      },

      getCurrentPosition() {
        this.locations = [];
        this.searchTerm = null;
        this.selectedGooglePlaceId = null;

        if (this.fetchedCurrentLocation) {
          this.$emit('geolocated', null);
        }
        else {
          this.fetchingCurrentLocation = true;
          window.navigator.geolocation.getCurrentPosition(this.getCurrentPositionSuccess, this.getCurrentPositionError, {
            timeout: 10000
          });
        }

        this.fetchedCurrentLocation = false;
      },

      getCurrentPositionError() {
        this.fetchedCurrentLocation = false;
        this.fetchingCurrentLocation = false;

        events.emit('notification', {
          type: 'error',
          message: 'Couldn\'t find your current location. Please check browser settings.'
        });
      },

      getCurrentPositionSuccess(result) {
        this.fetchedCurrentLocation = true;
        this.fetchingCurrentLocation = false;

        this.$refs.selectInput.forceUpdateSearchTerm(null);

        this.$emit('geolocated', {
          latitude: result.coords.latitude,
          longitude: result.coords.longitude,
          isCurrentLocation: true
        });
      },

      async googlePlaceSelected(placeId) {
        if (placeId) {
          try {
            this.loadingLocationData = true;

            const response = await this.$googleMapsService.getPlaceDetails(placeId);

            const { lat, lng } = response.geometry.location;

            this.$emit('geolocated', { latitude: lat, longitude: lng, isCurrentLocation: false });
          }

          catch (error) {
            logger.error(error);

            this.genericErrorHandler();
          }

          finally {
            this.loadingLocationData = false;
          }
        }
      },

      searchTermUpdateHandler(searchTerm) {
        this.searchTerm = searchTerm;

        if (!searchTerm) {
          this.locations = [];
          this.$emit('geolocated', null);
        }
        else {
          this.fetchedCurrentLocation = false;
          this.searchGooglePlaces();
        }
      },

      searchGooglePlaces: debounce(async function searchGooglePlaces() {
        try {
          if (this.searchTerm.length > 1) {
            this.locations = await this.$googleMapsService.getAutoCompletePredictions(this.searchTerm);
          }
          else {
            this.locations = [];
          }
        }
        catch (error) {
          logger.error(error);
        }
      }, 500),

      async unmatchedInputHandler(searchTerm) {
        try {
          this.loadingLocationData = true;

          if (searchTerm && searchTerm.length > 1) {
            const result = await this.$googleMapsService.getGeoLocation(searchTerm);

            if (result) {
              const { lat, lng } = result.geometry.location;

              this.$emit('geolocated', { latitude: lat, longitude: lng, isCurrentLocation: false });

              this.$refs.selectInput.forceUpdateSearchTerm(result.formatted_address);
            }
            else {
              this.genericErrorHandler();
            }
          }

          else {
            events.emit('notification', { type: 'error', message: 'Invalid location search.' });
          }
        }

        catch (error) {
          logger.error(error);

          this.genericErrorHandler();
        }

        finally {
          this.loadingLocationData = false;
        }
      }
    }
  };
</script>
